<script setup lang="ts">
import { onMounted, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import type { SavedReport } from '../../../interfaces';
import type { ApiResponse } from '@Types';
import { useADialog, useAToast } from '@Atoms';
import { useForm } from '@FormModule';
import { useSavedReports } from '../../../composables';
import { CreateSavedReportService } from '../../../services';
import useFields from './useFields';

const emit = defineEmits<{
  onSaved: [savedReport: SavedReport];
}>();

const props = defineProps<{
  dataTableId: string;
  isSaveAsNew?: boolean;
  sourceTemplateId?: string;
}>();

const { t } = useI18n();
const { notify, TOAST_TYPES } = useAToast();
const { reset } = useADialog();
const { fieldsValidation } = useForm();
const { getSnapshot, isReportManager, isSavedReportsOffError } = useSavedReports();
// NOTE :: Saving a copy of a shared report never re-shares it (that would only duplicate it); the copy can be shared
// later from the saved-reports list.
const CAN_SHARE = isReportManager.value && !props.sourceTemplateId;
const { currentSchemaModels, fieldsSchema, getField, setForm } = useFields(CAN_SHARE);

const DUPLICATE_NAME_ERROR = 'filters.savedReports.duplicateName';
const NAME_MAX_LENGTH = 100;
const NAME_TOO_LONG_ERROR = 'filters.savedReports.nameTooLong';
const REQUIRED_ERROR = 'components.forms.errors.required';
const SNAPSHOT = getSnapshot();
const PICKED_SELECT_FILTERS = [...new URLSearchParams(SNAPSHOT.select_filter).keys()];
const FILTERS_COUNT = Object.keys(SNAPSHOT.filters).length + PICKED_SELECT_FILTERS.length;
const TOAST_ERROR = { title: 'filters.savedReports.errorToast', type: TOAST_TYPES.ERROR };
const TOAST_FEATURE_OFF = { title: 'filters.savedReports.featureOffToast', type: TOAST_TYPES.WARNING };
const TOAST_SUCCESS = { title: 'filters.savedReports.savedToast', type: TOAST_TYPES.SUCCESS };

const loading = ref(false);

onMounted(setForm);

// EVENTS
// NOTE :: A second Enter while the first save is in flight does nothing, so one click can't make two reports.
// The name cap matches the api's `max:100`, and a name of only spaces counts as empty (the form's required check
// passes it, the api's does not); both show under the field instead of a generic error toast.
function onSubmit() {
  if (loading.value) return;

  const nameField = getField('name');

  nameField?.state.errors.delete(DUPLICATE_NAME_ERROR);
  nameField?.state.errors.delete(NAME_TOO_LONG_ERROR);
  nameField?.state.errors.delete(REQUIRED_ERROR);

  const hasErrors = fieldsValidation(fieldsSchema.value, currentSchemaModels.value);
  if (hasErrors) return;

  const name = String(nameField?.model ?? '').trim();

  if (!name) return nameField?.state.errors.add(REQUIRED_ERROR);

  const isNameTooLong = name.length > NAME_MAX_LENGTH;
  if (isNameTooLong) return nameField?.state.errors.add(NAME_TOO_LONG_ERROR);

  loading.value = true;

  const PAYLOAD = {
    ...SNAPSHOT,
    data_table_id: props.dataTableId,
    is_default: !!getField('is_default')?.model,
    shareable: !!getField('shareable')?.model,
    name,
    source_template_id: props.sourceTemplateId,
  };

  CreateSavedReportService(PAYLOAD)
    .then(onSaveSuccess)
    .catch(onSaveError)
    .finally(() => (loading.value = false));
}

function onSaveSuccess({ data }: ApiResponse<SavedReport>) {
  emit('onSaved', data);

  notify(TOAST_SUCCESS);

  reset();
}

// NOTE :: The api refuses a new report past a per-list cap — the person's own (`saved_reports_limit`) or the
// shelter's shared ones (`saved_reports_shared_limit`) — with the cap itself, so the message names the real number.
function onSaveError(error: any) {
  const responseData = error?.response?.data;

  const isLimitReached = responseData?.code === 'saved_reports_limit';
  if (isLimitReached) {
    const TOAST_LIMIT = {
      title: 'filters.savedReports.limitReachedTitle',
      message: t('filters.savedReports.limitReachedMessage', { limit: responseData.limit }),
      type: TOAST_TYPES.WARNING,
    };

    return notify(TOAST_LIMIT);
  }

  const isSharedLimitReached = responseData?.code === 'saved_reports_shared_limit';
  if (isSharedLimitReached) {
    const TOAST_SHARED_LIMIT = {
      title: 'filters.savedReports.sharedLimitReachedTitle',
      message: t('filters.savedReports.sharedLimitReachedMessage', { limit: responseData.limit }),
      type: TOAST_TYPES.WARNING,
    };

    return notify(TOAST_SHARED_LIMIT);
  }

  if (isSavedReportsOffError(error)) return notify(TOAST_FEATURE_OFF);

  const isDuplicateName = error?.response?.status === 409;
  if (!isDuplicateName) return notify(TOAST_ERROR);

  getField('name')?.state.errors.add(DUPLICATE_NAME_ERROR);
}
</script>

<template>
  <ADialogWrapper>
    <template #header>
      {{ $t(isSaveAsNew ? 'filters.savedReports.saveAsNewHeader' : 'filters.savedReports.saveHeader') }}
    </template>

    <FormTemplate
      :schema="fieldsSchema"
      @onFormSubmit="onSubmit"
    />

    <ul class="mt-4 flex flex-col gap-1 text-sm text-slate-500">
      <li>{{ $t('filters.savedReports.summaryFilters', { count: FILTERS_COUNT }) }}</li>

      <li>{{ $t('filters.savedReports.summaryColumns', { count: SNAPSHOT.columns.length }) }}</li>

      <li>{{ $t('filters.savedReports.summarySearch') }}</li>
    </ul>

    <template #footer>
      <Button
        class="af-button"
        severity="secondary"
        :disabled="loading"
        :label="$t('common.cancel')"
        @click="reset"
      />

      <Button
        class="af-button af-button-success"
        icon="pi pi-check"
        :label="$t('common.save')"
        :loading
        @click="onSubmit"
      />
    </template>
  </ADialogWrapper>
</template>
